import type { EmailBlock } from '@/types/email-blocks';
import type { DragData } from '@/components/email-builder/DndProvider';
import { blockRegistry } from './block-registry';
import { canAcceptDrop } from './dnd-validation';
import { findBlockById } from './block-tree-utils';

/**
 * A pre-calculated rectangular region where a drop would insert a block
 * at `{ parentId, index }`. Regions are computed once on drag start and
 * remain static for the entire drag session.
 */
export interface DropRegion {
  /** Parent block ID (null = document root) */
  parentId: string | null;
  /** Insertion index among siblings */
  index: number;
  /** Bounding rectangle in viewport coordinates */
  rect: DOMRect;
  /** Nesting depth — deeper regions are checked first so container
   *  drop zones take priority over outer ones. */
  depth: number;
  /** Axis along which siblings are laid out */
  axis: 'vertical' | 'horizontal';
}

/**
 * Compute all drop regions for the current block tree.
 *
 * The algorithm walks the tree, measures each block's DOM element via
 * `[data-block-id]`, and produces N+1 regions for N sibling blocks at each
 * level. Adjacent regions share a boundary at the midpoint of the block
 * between them:
 *
 *   Region 0:  top-of-container  →  midpoint-of-block-0
 *   Region 1:  midpoint-of-block-0  →  midpoint-of-block-1
 *   …
 *   Region N:  midpoint-of-block-(N-1)  →  bottom-of-container
 *
 * For horizontal layouts (section → columns) the same logic applies on
 * the X axis.
 *
 * Regions are pre-filtered with `canAcceptDrop` so the hit-test at drag
 * time never returns an invalid target.
 *
 * @param blockTree     Current block tree from the store
 * @param dragData      Info about the block being dragged
 * @param draggedBlockId  ID of the block being dragged (null for new blocks)
 *                        — excluded from measurement so it doesn't occupy
 *                        space in the region map.
 */
export function calculateDropRegions(
  blockTree: EmailBlock[],
  dragData: DragData,
  draggedBlockId: string | null,
): DropRegion[] {
  const regions: DropRegion[] = [];

  // Build regions for root-level blocks
  buildSiblingRegions(
    blockTree,
    null,  // parentId
    0,     // depth
    'vertical',
    dragData,
    draggedBlockId,
    regions,
    blockTree, // full tree for validation
  );

  return regions;
}

// ─── Internal helpers ────────────────────────────────────────────────

/**
 * Build drop regions for a list of sibling blocks inside a given parent.
 * Recurses into container blocks to build child-level regions.
 */
function buildSiblingRegions(
  siblings: EmailBlock[],
  parentId: string | null,
  depth: number,
  axis: 'vertical' | 'horizontal',
  dragData: DragData,
  draggedBlockId: string | null,
  out: DropRegion[],
  fullTree: EmailBlock[],
): void {
  // Filter out the block currently being dragged so it doesn't take up
  // space in the region map.
  const visibleSiblings = draggedBlockId
    ? siblings.filter(b => b.id !== draggedBlockId)
    : siblings;

  // Sort by orderIndex
  const sorted = [...visibleSiblings].sort((a, b) => a.orderIndex - b.orderIndex);

  // Measure each sibling's DOM rect
  const measured: Array<{ block: EmailBlock; rect: DOMRect; originalIndex: number }> = [];
  for (let i = 0; i < sorted.length; i++) {
    const block = sorted[i];
    const el = document.querySelector(`[data-block-id="${block.id}"]`);
    if (!el) continue;
    measured.push({ block, rect: el.getBoundingClientRect(), originalIndex: i });
  }

  // Determine the container rect — needed for the first and last region
  // boundaries, and for the empty-container case.
  const containerRect = getContainerRect(parentId, measured, axis);
  if (!containerRect) return;

  if (measured.length === 0) {
    // Empty container → single region covering the whole container
    addRegionIfValid(out, {
      parentId,
      index: 0,
      rect: containerRect,
      depth,
      axis,
    }, dragData, fullTree);
  } else {
    // Build N+1 regions by splitting at midpoints
    for (let i = 0; i <= measured.length; i++) {
      const regionRect = computeSplitRect(
        containerRect,
        measured,
        i,
        axis,
      );
      if (!regionRect) continue;

      // Compute the insertion index. Because the dragged block was removed
      // from `sorted`, we need to figure out where `i` maps to in the
      // original (full) sibling list.
      const insertionIndex = computeInsertionIndex(sorted, i, siblings, draggedBlockId);

      addRegionIfValid(out, {
        parentId,
        index: insertionIndex,
        rect: regionRect,
        depth,
        axis,
      }, dragData, fullTree);
    }
  }

  // Recurse into container blocks (those that allow children)
  for (const { block } of measured) {
    const config = blockRegistry.get(block.blockType);
    if (!config?.allowChildren) continue;

    const children = block.childBlocks
      ? [...block.childBlocks].sort((a, b) => a.orderIndex - b.orderIndex)
      : [];

    // Determine axis for children: sections lay out columns horizontally
    const childAxis = block.blockType === 'section' ? 'horizontal' : 'vertical';

    buildSiblingRegions(
      children,
      block.id,
      depth + 1,
      childAxis,
      dragData,
      draggedBlockId,
      out,
      fullTree,
    );
  }
}

/**
 * Get the bounding rect for a container.
 *
 * For root-level (parentId === null), we use `.lw-canvas-email-container`.
 * For a block container, we look for the block's DOM element.
 */
function getContainerRect(
  parentId: string | null,
  measured: Array<{ rect: DOMRect }>,
  axis: 'vertical' | 'horizontal',
): DOMRect | null {
  if (parentId === null) {
    const el = document.querySelector('.lw-canvas-email-container');
    if (el) return el.getBoundingClientRect();
    // Fallback: synthesise from children
    return synthesiseContainerRect(measured, axis);
  }

  const el = document.querySelector(`[data-block-id="${parentId}"]`);
  if (el) return el.getBoundingClientRect();
  return synthesiseContainerRect(measured, axis);
}

/**
 * Fallback: synthesise a container rect from its children.
 */
function synthesiseContainerRect(
  measured: Array<{ rect: DOMRect }>,
  axis: 'vertical' | 'horizontal',
): DOMRect | null {
  if (measured.length === 0) return null;

  if (axis === 'vertical') {
    const top = Math.min(...measured.map(m => m.rect.top));
    const bottom = Math.max(...measured.map(m => m.rect.bottom));
    const left = Math.min(...measured.map(m => m.rect.left));
    const right = Math.max(...measured.map(m => m.rect.right));
    return new DOMRect(left, top, right - left, bottom - top);
  } else {
    const left = Math.min(...measured.map(m => m.rect.left));
    const right = Math.max(...measured.map(m => m.rect.right));
    const top = Math.min(...measured.map(m => m.rect.top));
    const bottom = Math.max(...measured.map(m => m.rect.bottom));
    return new DOMRect(left, top, right - left, bottom - top);
  }
}

/**
 * Compute the bounding rect for region `i` among `measured.length + 1` regions.
 *
 * Region boundaries:
 *   i === 0:             container-start  →  midpoint of block[0]
 *   0 < i < N:           midpoint of block[i-1]  →  midpoint of block[i]
 *   i === N:             midpoint of block[N-1]  →  container-end
 */
function computeSplitRect(
  containerRect: DOMRect,
  measured: Array<{ rect: DOMRect }>,
  i: number,
  axis: 'vertical' | 'horizontal',
): DOMRect | null {
  const n = measured.length;

  if (axis === 'vertical') {
    const left = containerRect.left;
    const width = containerRect.width;

    const top = i === 0
      ? containerRect.top
      : midpoint(measured[i - 1].rect.top, measured[i - 1].rect.bottom);

    const bottom = i === n
      ? containerRect.bottom
      : midpoint(measured[i].rect.top, measured[i].rect.bottom);

    if (bottom <= top) return null;
    return new DOMRect(left, top, width, bottom - top);
  } else {
    // Horizontal axis (sections → columns)
    const top = containerRect.top;
    const height = containerRect.height;

    const left = i === 0
      ? containerRect.left
      : midpoint(measured[i - 1].rect.left, measured[i - 1].rect.right);

    const right = i === n
      ? containerRect.right
      : midpoint(measured[i].rect.left, measured[i].rect.right);

    if (right <= left) return null;
    return new DOMRect(left, top, right - left, height);
  }
}

function midpoint(a: number, b: number): number {
  return (a + b) / 2;
}

/**
 * Map a position `i` in the filtered (visible) sibling list back to the
 * correct insertion index in the full sibling list.
 *
 * When the dragged block is removed from the list, later indices shift.
 * This function accounts for that so the store receives the right index.
 */
function computeInsertionIndex(
  filteredSorted: EmailBlock[],
  positionInFiltered: number,
  allSiblings: EmailBlock[],
  draggedBlockId: string | null,
): number {
  if (!draggedBlockId) return positionInFiltered;

  // Find where the dragged block sits among all siblings
  const allSorted = [...allSiblings].sort((a, b) => a.orderIndex - b.orderIndex);
  const draggedIdx = allSorted.findIndex(b => b.id === draggedBlockId);
  if (draggedIdx === -1) return positionInFiltered;

  // If inserting after the dragged block's original position, add 1
  // to account for its removal.
  if (positionInFiltered > draggedIdx) {
    return positionInFiltered + 1;
  }
  return positionInFiltered;
}

/**
 * Converts a region's insertion index to the index a move expects.
 *
 * Region indexes (and the ghost placeholder) count positions with the dragged block still in
 * place, while a move inserts among the remaining siblings. Within the same parent, positions
 * after the block's current one are therefore one lower.
 */
export function toMoveIndex(dropIndex: number, currentIndex: number, sameParent: boolean): number {
  return sameParent && dropIndex > currentIndex ? dropIndex - 1 : dropIndex;
}

/**
 * Only add the region if canAcceptDrop says it's valid.
 */
function addRegionIfValid(
  out: DropRegion[],
  region: DropRegion,
  dragData: DragData,
  blockTree: EmailBlock[],
): void {
  const validation = canAcceptDrop(dragData, region, blockTree);
  if (!validation.isValid) return;
  out.push(region);
}

/**
 * Hit-test a pointer coordinate against the pre-calculated regions.
 *
 * Returns the deepest matching region (highest `depth`), giving
 * container drop zones priority over outer ones.
 *
 * @param regions   The static region map
 * @param pointerX  Viewport X
 * @param pointerY  Viewport Y
 * @returns The matched region or null
 */
export function hitTestRegions(
  regions: DropRegion[],
  pointerX: number,
  pointerY: number,
): DropRegion | null {
  let best: DropRegion | null = null;

  for (const region of regions) {
    const r = region.rect;
    if (
      pointerX >= r.left &&
      pointerX <= r.right &&
      pointerY >= r.top &&
      pointerY <= r.bottom
    ) {
      if (!best || region.depth > best.depth) {
        best = region;
      }
    }
  }

  return best;
}
